import api from "@/services/api";
import { FilterValues } from "@/components/ui/FilterBar/types";

/* ─── Tipos da resposta ──────────────────────────────────────────────────── */

export interface SolicitacaoCard {
  purchaseRequestId: string;
  buildingCode: string;
  buildingName: string;
  costCenterId: string;
  costCenterName: string;
  itemCount: number;
  requestDate: string;
  maxCompetenceLevel: number;
  /** Prévia de linhas de item (retorno do kanban-compras). */
  itemsPreview?: string[];
}

/**
 * O Sienge pode mandar o id do centro de custo em `buildingCode` (só dígitos) sem `buildingName`,
 * com `costCenterId` vazio. Nesse caso o id útil para `/cost-centers` é o de `buildingCode`.
 */
export function effectiveSolicitacaoCostCenterId(
  c: Pick<SolicitacaoCard, "costCenterId" | "buildingCode" | "buildingName">
): string {
  const cid = (c.costCenterId ?? "").trim();
  if (cid) return cid;
  const bc = (c.buildingCode ?? "").trim();
  const bn = (c.buildingName ?? "").trim();
  if (bc && /^\d+$/.test(bc) && !bn) return bc;
  return "";
}

/** Texto da linha “Obra” do card — evita mostrar só o número que na verdade é id de centro de custo. */
export function buildSolicitacaoObraLine(c: Pick<SolicitacaoCard, "buildingCode" | "buildingName">): string {
  const bn = (c.buildingName ?? "").trim();
  const bc = (c.buildingCode ?? "").trim();
  if (bn && bc) return `${bc} · ${bn}`;
  if (bn) return bn;
  if (bc && /^\d+$/.test(bc) && !bn) return "Obra —";
  if (bc) return bc;
  return "Obra —";
}

/** Linha normalizada para grade / modal de itens da SC (Sienge all/items). */
export interface PurchaseRequestItemRow {
  itemNumber: string;
  productId: string;
  description: string;
  quantity: number;
  unit: string;
  competenceLevel: number | null;
  authorized: boolean | null;
  openQuantity: number | null;
}

function normalizePurchaseRequestItem(raw: Record<string, unknown>): PurchaseRequestItemRow {
  const itemNumber = String(raw.itemNumber ?? raw.item ?? raw.lineNumber ?? "").trim();
  const productId = String(
    raw.productId ?? raw.product_id ?? raw.resourceId ?? raw.resource_id ?? raw.resourceCode ?? ""
  ).trim();
  const description = String(
    raw.description ??
      raw.itemDescription ??
      raw.materialDescription ??
      raw.productDescription ??
      raw.product_description ??
      raw.detailDescription ??
      raw.materialName ??
      raw.productName ??
      raw.name ??
      ""
  ).trim();
  const quantity = Number(raw.quantity ?? raw.requestedQuantity ?? raw.openQuantity ?? 0) || 0;
  const unit = String(
    raw.unitOfMeasure ?? raw.unitySymbol ?? raw.unitSymbol ?? raw.unit ?? ""
  ).trim();
  const lvlRaw = Number(raw.competenceLevel);
  const competenceLevel = Number.isNaN(lvlRaw) ? null : lvlRaw;
  const auth = raw.authorized;
  const authorized =
    typeof auth === "boolean"
      ? auth
      : typeof auth === "string"
        ? auth.toLowerCase() === "true"
        : null;
  const oqRaw = Number(raw.openQuantity);
  const openQuantity = Number.isNaN(oqRaw) ? null : oqRaw;
  return { itemNumber, productId, description, quantity, unit, competenceLevel, authorized, openQuantity };
}

/**
 * Todos os itens de uma solicitação de compras (paginação interna).
 * Usa `skipStatusFilter` para incluir itens fora de PENDING (ex.: coluna Gestor).
 */
export async function fetchPurchaseRequestItemsAll(
  purchaseRequestId: string,
  period?: { startDate: string; endDate: string }
): Promise<PurchaseRequestItemRow[]> {
  const all: PurchaseRequestItemRow[] = [];
  const limit = 100;
  let offset = 0;
  for (let page = 0; page < 40; page++) {
    const params: Record<string, string | number> = {
      purchaseRequestId,
      limit,
      offset,
      skipStatusFilter: "true",
    };
    if (period?.startDate) params.startDate = period.startDate;
    if (period?.endDate) params.endDate = period.endDate;

    const { data } = await api.get("/api/purchase-requests/items", { params });
    const list: unknown[] = Array.isArray(data.data) ? data.data : [];
    for (const row of list) {
      if (row != null && typeof row === "object") {
        all.push(normalizePurchaseRequestItem(row as Record<string, unknown>));
      }
    }
    if (list.length < limit) break;
    offset += limit;
  }
  return all;
}

export interface InsumoPrecoRef {
  price: number;
  orderDate: string;
  orderId: string;
}

export interface InsumoPrecos {
  ultimoPreco: InsumoPrecoRef | null;
  menorPreco: InsumoPrecoRef | null;
}

/** Normaliza ID de insumo para comparação (remove zeros à esquerda de valores numéricos). */
function normInsumoId(v: string): string {
  const t = String(v ?? "").trim();
  if (!t) return "";
  return /^\d+$/.test(t) ? String(Number(t)) : t.toLowerCase();
}

/** Janela do histórico de preços do modal da SC (independente do filtro da aba). */
const INSUMO_PRECOS_HIST_MONTHS = 6;

let insumoPrecosHistCache: { key: string; items: OrderItem[] } | null = null;

/**
 * Itens de pedidos dos últimos 6 meses (todas as obras) para o histórico de preços da SC.
 * Pagina `/api/pedidos-compra/items-by-period` (cache de pedidos/itens no servidor) e
 * entrega resultados parciais via `onPage`. Cache de sessão por janela de datas.
 */
export async function fetchOrderItemsHistorico(
  onPage?: (accumulated: OrderItem[]) => void
): Promise<OrderItem[]> {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - INSUMO_PRECOS_HIST_MONTHS);
  const startDate = start.toISOString().slice(0, 10);
  const endDate = end.toISOString().slice(0, 10);
  const key = `${startDate}|${endDate}`;
  if (insumoPrecosHistCache?.key === key) return insumoPrecosHistCache.items;

  const filters: FilterValues = {
    startDate,
    endDate,
    costCenter: [],
    constructiveUnit: [],
    itemFilter: [],
    quotationFilter: "",
    purchaseRequestFilter: "",
    orderFilter: "",
    supplierFilter: [],
  };

  const all: OrderItem[] = [];
  let offset = 0;
  for (let i = 0; i < 60; i++) {
    const page = await fetchItemsByPeriodPage(filters, { offset, limit: 60 });
    all.push(...page.items);
    onPage?.([...all]);
    if (!page.hasMore) break;
    offset = page.nextOffset;
  }
  insumoPrecosHistCache = { key, items: all };
  return all;
}

/**
 * Último preço e menor preço por insumo a partir de itens de pedidos de compra.
 * Match estrito: SC.productId ↔ PC.resourceId.
 * Preço considerado: netPrice quando > 0, senão unitPrice.
 */
export function computeInsumoPrecos(
  orderItems: OrderItem[],
  productIds: string[]
): Map<string, InsumoPrecos> {
  const out = new Map<string, InsumoPrecos>();
  const byNorm = new Map<string, string>();
  for (const id of productIds) {
    const trimmed = id.trim();
    if (!trimmed) continue;
    out.set(trimmed, { ultimoPreco: null, menorPreco: null });
    byNorm.set(normInsumoId(trimmed), trimmed);
  }
  if (byNorm.size === 0) return out;

  for (const it of orderItems) {
    const rid = normInsumoId(it.resourceId);
    if (!rid) continue;
    const pid = byNorm.get(rid);
    if (!pid) continue;
    const price = it.netPrice > 0 ? it.netPrice : it.unitPrice;
    if (!(price > 0)) continue;
    const ref: InsumoPrecoRef = {
      price,
      orderDate: (it.orderDate ?? "").slice(0, 10),
      orderId: it.orderId,
    };
    const agg = out.get(pid)!;
    if (!agg.ultimoPreco || ref.orderDate > agg.ultimoPreco.orderDate) agg.ultimoPreco = ref;
    if (!agg.menorPreco || price < agg.menorPreco.price) agg.menorPreco = ref;
  }
  return out;
}

export interface QuotationItemRow {
  itemNumber: string;
  productId: string;
  description: string;
  quantity: string;
  unit: string;
}

function normalizeQuotationItemRow(raw: Record<string, unknown>): QuotationItemRow {
  const itemNumber = String(
    raw.itemNumber ??
      raw.item_number ??
      raw.number ??
      raw.purchaseQuotationItemNumber ??
      raw.lineNumber ??
      raw.line ??
      ""
  ).trim();
  const productId = String(
    raw.productId ?? raw.product_id ?? raw.resourceId ?? raw.resource_id ?? raw.insumoId ?? raw.insumo_id ?? ""
  ).trim();
  const description = String(
    raw.productDescription ??
      raw.product_description ??
      raw.description ??
      raw.resourceDescription ??
      raw.resource_description ??
      raw.detailDescription ??
      ""
  ).trim();
  const qtyRaw = raw.quantity ?? raw.amount ?? raw.requestedQuantity ?? raw.openQuantity;
  const quantity = qtyRaw != null && String(qtyRaw).trim() !== "" ? String(qtyRaw).trim() : "";
  const unit = String(
    raw.unitySymbol ?? raw.unitSymbol ?? raw.unitOfMeasure ?? raw.unit_of_measure ?? raw.unit ?? ""
  ).trim();
  return { itemNumber, productId, description, quantity, unit };
}

/** Itens de uma cotação (API interna → Sienge). */
export async function fetchQuotationItems(quotationId: string): Promise<QuotationItemRow[]> {
  const id = encodeURIComponent(String(quotationId).trim());
  if (!id) return [];
  const { data } = await api.get(`/api/suprimentos/purchase-quotations/${id}/items`);
  const list: unknown[] = Array.isArray(data.data) ? data.data : [];
  const out: QuotationItemRow[] = [];
  for (const row of list) {
    if (row != null && typeof row === "object") {
      const r = normalizeQuotationItemRow(row as Record<string, unknown>);
      if (r.itemNumber || r.productId || r.description) out.push(r);
    }
  }
  return out;
}

export interface CotacaoCard {
  purchaseQuotationId: string;
  label: string;
  status: string;
  supplierName: string;
  date: string;
  itemCount: number;
  costCenterId: string;
  costCenterName: string;
}

export interface PedidoCard {
  purchaseOrderId: string;
  orderNumber: string | number;
  supplier: string;
  date: string;
  status: string;
  value: number;
  itemCount: number;
  hasFinancialGenerated: boolean;
  received: boolean;
  accountsPayablePaymentStatus: string;
  costCenterId: string;
  costCenterName: string;
  /** Observações / notas do pedido (Sienge), quando existirem */
  observations?: string;
  /** Labels dos itens do pedido (ex.: "1. CIMENTO – 50KG") */
  itemsPreview?: string[];
}

/** Códigos em inglês retornados pelo Sienge → rótulo em português (exibição no Kanban). */
const PEDIDO_SIENGE_STATUS_PT: Record<string, string> = {
  PENDING: "Pendente",
  APPROVED: "Aprovado",
  REJECTED: "Rejeitado",
  CANCELLED: "Cancelado",
  CANCELED: "Cancelado",
  CLOSED: "Encerrado",
  OPEN: "Aberto",
  DRAFT: "Rascunho",
  FULLY_DELIVERED: "Totalmente entregue",
  PARTIALLY_DELIVERED: "Parcialmente entregue",
  IN_PROGRESS: "Em andamento",
  WAITING_APPROVAL: "Aguardando aprovação",
  RELEASED: "Liberado",
  INVOICED: "Faturado",
};

/**
 * Traduz o status bruto do pedido (Sienge) para português quando o código for conhecido.
 * Valores já em português ou desconhecidos são mantidos.
 */
export function labelPedidoSiengeStatus(status: string): string {
  const t = (status ?? "").trim();
  if (!t) return "—";
  const k = t.toUpperCase().replace(/-/g, "_").replace(/\s+/g, "_");
  return PEDIDO_SIENGE_STATUS_PT[k] ?? t;
}

/* ─── Itens de pedido (insumos) ──────────────────────────────────────────── */

export interface OrderItem {
  orderId:     string;
  orderDate?:  string;
  /** Credor / fornecedor do pedido (cabeçalho). */
  creditorName?: string;
  itemNumber:  string;
  code:        string;
  /** ID do insumo no Sienge — usado para vincular à SC (productId). */
  resourceId:  string;
  description: string;
  quantity:    number;
  /** Preço bruto unitário */
  unitPrice:   number;
  /** Preço líquido unitário (após desconto) — usar para cálculos financeiros */
  netPrice:    number;
  /** Valor total = quantity × netPrice */
  totalAmount: number;
  unit:        string;
}

function normalizeItem(raw: Record<string, unknown>, orderId: string): OrderItem {
  // Sienge pedidos: campo real é `resourceCode` / `resourceId` para o código
  const code = String(
    raw.resourceCode ?? raw.resource ?? raw.code ?? raw.codigo ?? raw.productId ?? raw.resourceId ?? ""
  ).trim();

  // ID do insumo (Sienge `resourceId`) — corresponde ao `productId` da solicitação
  const resourceId = String(raw.resourceId ?? raw.resource_id ?? "").trim();

  // Sienge pedidos: `resourceDescription` é a descrição do insumo
  const description = String(
    raw.resourceDescription ?? raw.productDescription ?? raw.description ??
    raw.detailDescription ?? raw.productName ?? raw.materialDescription ??
    raw.serviceDescription ?? raw.itemDescription ?? raw.name ?? ""
  ).trim();

  // Sienge pedidos: `itemNumber` é o número de linha do item
  const itemNumber = String(raw.itemNumber ?? raw.number ?? raw.item_number ?? raw.lineNumber ?? "").trim();

  // Sienge pedidos: `quantity` é a quantidade comprada
  const quantity = Number(raw.quantity ?? raw.purchasedQuantity ?? raw.requestedQuantity ?? 0) || 0;

  // Sienge pedidos: `unitPrice` = preço bruto; `netPrice` = preço efetivo (após desconto)
  const unitPrice = Number(raw.unitPrice ?? raw.unitaryValue ?? raw.unit_price ?? raw.pricePerUnit ?? 0) || 0;
  const netPrice  = Number(raw.netPrice  ?? raw.net_price ?? unitPrice) || unitPrice;

  // Sienge NÃO retorna campo de total calculado — computamos aqui
  // Usamos netPrice (preço com desconto) para refletir o valor real pago
  const explicitTotal = Number(
    raw.totalPrice ?? raw.totalValue ?? raw.amount ?? raw.totalAmount ??
    raw.grossAmount ?? raw.netAmount ?? raw.total ?? raw.valor ?? 0
  );
  const totalAmount = explicitTotal > 0 ? explicitTotal : quantity * netPrice;

  // Sienge pedidos: campo real da unidade é `unitOfMeasure`
  const unit = String(
    raw.unitOfMeasure ?? raw.unitySymbol ?? raw.unitSymbol ?? raw.unit_of_measure ?? raw.unit ?? ""
  ).trim();

  const orderDate =
    String(
      raw.orderDate ??
        raw.order_date ??
        raw.issueDate ??
        raw.emissionDate ??
        raw.purchaseDate ??
        ""
    ).trim() || undefined;

  const creditorRaw = String(
    raw.creditorName ?? raw.supplierName ?? raw.businessPartnerName ?? raw.personName ?? ""
  ).trim();
  const creditorName = creditorRaw && creditorRaw !== "—" ? creditorRaw : undefined;

  return {
    orderId,
    orderDate,
    creditorName,
    itemNumber,
    code,
    resourceId,
    description,
    quantity,
    unitPrice,
    netPrice,
    totalAmount,
    unit,
  };
}

export interface ItemsByPeriodPageResult {
  items: OrderItem[];
  ordersTotal: number;
  ordersOffset: number;
  hasMore: boolean;
  nextOffset: number;
}

export interface ItemsByResourceUcPageResult {
  items: OrderItem[];
  ordersTotal: number;
  ordersScanOffset: number;
  nextScanOffset: number;
  hasMoreScans: boolean;
}

/**
 * Itens de pedido no período que casam o insumo e têm apropriação na unidade construtiva informada.
 * Varre pedidos da obra em lotes (`ordersScanOffset` / `ordersScanLimit`); use `hasMoreScans` para paginar.
 */
export async function fetchItemsByResourceAndConstructiveUnit(params: {
  startDate: string;
  endDate: string;
  costCenterId: string;
  constructiveUnitName: string;
  /** ID Sienge da UC — prioriza cruzamento com apropriações do pedido quando informado */
  constructiveUnitId?: string;
  resourceId: string;
  resourceDescription: string;
  ordersScanOffset?: number;
  ordersScanLimit?: number;
}): Promise<ItemsByResourceUcPageResult> {
  const { data } = await api.get("/api/pedidos-compra/items-by-resource-and-uc", {
    params: {
      startDate: params.startDate,
      endDate: params.endDate,
      costCenterId: params.costCenterId,
      constructiveUnitName: params.constructiveUnitName,
      ...(params.constructiveUnitId?.trim()
        ? { constructiveUnitId: params.constructiveUnitId.trim() }
        : {}),
      resourceId: params.resourceId,
      resourceDescription: params.resourceDescription,
      ordersScanOffset: String(params.ordersScanOffset ?? 0),
      ordersScanLimit: String(params.ordersScanLimit ?? 80),
    },
    timeout: 120_000,
  });
  const list: unknown[] = Array.isArray(data.data) ? data.data : [];
  const items = list
    .filter((r): r is Record<string, unknown> => r != null && typeof r === "object")
    .map((r) => normalizeItem(r, String(r.orderId ?? "")));
  return {
    items,
    ordersTotal: Number(data.ordersTotal) || 0,
    ordersScanOffset: Number(data.ordersScanOffset) || 0,
    nextScanOffset: Number(data.nextScanOffset) || 0,
    hasMoreScans: Boolean(data.hasMoreScans),
  };
}

/**
 * Uma página de itens de pedidos do período (por lote de pedidos).
 * Use `hasMore` / `nextOffset` para carregar o restante sem estourar tempo de resposta.
 */
export async function fetchItemsByPeriodPage(
  filters: FilterValues,
  page: { offset: number; limit?: number }
): Promise<ItemsByPeriodPageResult> {
  const params: Record<string, string> = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    ordersOffset: String(page.offset),
    ordersLimit: String(page.limit ?? 60),
  };
  if (Array.isArray(filters.costCenter) && filters.costCenter.length > 0) {
    params.costCenterId = filters.costCenter[0];
  }
  const { data } = await api.get("/api/pedidos-compra/items-by-period", {
    params,
    timeout: 120_000,
  });
  const list: unknown[] = Array.isArray(data.data) ? data.data : [];
  const items = list
    .filter((r): r is Record<string, unknown> => r != null && typeof r === "object")
    .map((r) => normalizeItem(r, String(r.orderId ?? "")));
  return {
    items,
    ordersTotal: Number(data.ordersTotal) || 0,
    ordersOffset: Number(data.ordersOffset) || 0,
    hasMore: Boolean(data.hasMore),
    nextOffset: Number(data.nextOffset) || 0,
  };
}

/**
 * Busca todos os itens do período (várias páginas). Prefira `fetchItemsByPeriodPage` na UI para períodos longos.
 */
export async function fetchItemsByPeriod(filters: FilterValues): Promise<OrderItem[]> {
  const out: OrderItem[] = [];
  let offset = 0;
  const limit = 80;
  for (let i = 0; i < 200; i++) {
    const page = await fetchItemsByPeriodPage(filters, { offset, limit });
    out.push(...page.items);
    if (!page.hasMore) break;
    offset = page.nextOffset;
  }
  return out;
}

/** Busca itens de vários pedidos: GET /api/pedidos-compra/items?orderIds=... */
export async function fetchItemsByOrderIds(orderIds: string[]): Promise<OrderItem[]> {
  if (orderIds.length === 0) return [];
  const { data } = await api.get("/api/pedidos-compra/items", {
    params: { orderIds: orderIds.join(",") },
  });
  const list: unknown[] = Array.isArray(data.data) ? data.data : [];
  return list
    .filter((r): r is Record<string, unknown> => r != null && typeof r === "object")
    .map((r) => normalizeItem(r, String(r.orderId ?? "")));
}

/** Busca itens de um único pedido: GET /api/purchase-orders/:orderId/items */
export async function fetchSingleOrderItems(orderId: string): Promise<OrderItem[]> {
  const { data } = await api.get(`/api/purchase-orders/${orderId}/items`);
  const list: unknown[] = Array.isArray(data.data) ? data.data : [];
  return list
    .filter((r): r is Record<string, unknown> => r != null && typeof r === "object")
    .map((r) => normalizeItem(r, orderId));
}

/** Resumo do pedido (cabeçalho + itens) para PDF / detalhe a partir do modal de pagamentos. */
export interface PurchaseOrderSummary {
  purchaseOrderId: string;
  orderNumber: string | number;
  supplier: string;
  date: string;
  status: string;
  value: number;
  observations: string;
  costCenterId: string;
  costCenterName: string;
  items: OrderItem[];
}

/** GET /api/purchase-orders/:orderId/summary — id interno ou nº do documento. */
export async function fetchPurchaseOrderSummary(orderRef: string): Promise<PurchaseOrderSummary> {
  const ref = String(orderRef ?? "").trim();
  if (!ref) throw new Error("Número do pedido obrigatório.");

  try {
    const { data } = await api.get(`/api/purchase-orders/${encodeURIComponent(ref)}/summary`);
    if (!data?.success || !data.data) {
      throw new Error(
        typeof data?.error === "string" && data.error.trim()
          ? data.error
          : "Pedido de compra não encontrado."
      );
    }

    const raw = data.data as Record<string, unknown>;
    const purchaseOrderId = String(raw.purchaseOrderId ?? ref).trim() || ref;
    const list: unknown[] = Array.isArray(raw.items) ? raw.items : [];
    const items = list
      .filter((r): r is Record<string, unknown> => r != null && typeof r === "object")
      .map((r) => normalizeItem(r, purchaseOrderId));

    return {
      purchaseOrderId,
      orderNumber: (raw.orderNumber as string | number) ?? purchaseOrderId,
      supplier: String(raw.supplier ?? "").trim(),
      date: String(raw.date ?? "").trim(),
      status: String(raw.status ?? "").trim(),
      value: Number(raw.value ?? 0) || 0,
      observations: String(raw.observations ?? "").trim(),
      costCenterId: String(raw.costCenterId ?? "").trim(),
      costCenterName: String(raw.costCenterName ?? "").trim(),
      items,
    };
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { error?: string } }; message?: string };
    const apiErr = ax.response?.data?.error;
    if (typeof apiErr === "string" && apiErr.trim()) throw new Error(apiErr);
    if (e instanceof Error) throw e;
    throw new Error("Falha ao buscar pedido de compra.");
  }
}

export interface KanbanComprasData {
  solPendente:             SolicitacaoCard[];
  solAprovada:             SolicitacaoCard[];
  cotacoes:                CotacaoCard[];
  pedidosSemFinanceiro:    PedidoCard[];
  pedidosGeradoFinanceiro: PedidoCard[];
}

/* ─── Chamadas de API ────────────────────────────────────────────────────── */

/**
 * Busca o Kanban completo de Suprimentos.
 * Endpoint: GET /api/suprimentos/kanban-compras
 * Parâmetros: startDate, endDate, costCenterId (opcional)
 *
 * Atenção: pode ser lento (envolve múltiplas chamadas ao Sienge).
 */
export async function fetchKanbanCompras(
  filters: FilterValues
): Promise<KanbanComprasData> {
  const params: Record<string, string> = {
    startDate: filters.startDate,
    endDate:   filters.endDate,
  };
  // Envia IDs separados por vírgula; o backend filtra pelo primeiro ID compatível
  const costCenterArr = Array.isArray(filters.costCenter) ? filters.costCenter : [];
  if (costCenterArr.length > 0) {
    params.costCenterId = costCenterArr.join(",");
  }

  const { data } = await api.get("/api/suprimentos/kanban-compras", { params });

  return {
    solPendente:             Array.isArray(data.solPendente)             ? data.solPendente             : [],
    solAprovada:             Array.isArray(data.solAprovada)             ? data.solAprovada             : [],
    cotacoes:                Array.isArray(data.cotacoes)                ? data.cotacoes                : [],
    pedidosSemFinanceiro:    Array.isArray(data.pedidosSemFinanceiro)    ? data.pedidosSemFinanceiro    : [],
    pedidosGeradoFinanceiro: Array.isArray(data.pedidosGeradoFinanceiro) ? data.pedidosGeradoFinanceiro : [],
  };
}

/**
 * Busca pedidos de compra no período.
 * Endpoint: GET /api/pedidos-compra
 */
export async function fetchPedidosCompra(
  filters: FilterValues
): Promise<unknown[]> {
  const params: Record<string, string> = {
    startDate: filters.startDate,
    endDate:   filters.endDate,
  };
  const { data } = await api.get("/api/pedidos-compra", { params });
  return Array.isArray(data.data) ? data.data : [];
}
